import { type CSSProperties, type PropsWithChildren, useState } from 'react';
import * as Mapbox from 'react-map-gl';
import { type ViewStateChangeEvent } from 'react-map-gl';
import useMapStyle from './useMapStyle';
import StyleControl from './StyleControl';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { SearchBox } from '@mapbox/search-js-react';
import mapboxgl, { Map as MapboxGLMap } from 'mapbox-gl';

// Constants ----

const ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;
const DEFAULT_LONGITUDE = -2.895;
const DEFAULT_LATITUDE = 54.093;
const DEFAULT_ZOOM = 5;

// Types ----

type MapStyle = {
  name: string;
  url: string;
};

type MapProps = {
  height: CSSProperties['height'];
  defaultMapStyle?: MapStyle | string | null;
  initialViewState?: Mapbox.MapProps['initialViewState'] | null;
  onViewChanged?: (event: ViewStateChangeEvent) => void;
  hideFeatures?: string[];
  showSearchBox?: boolean;
};

// Component ----

function Map({
  height,
  defaultMapStyle,
  initialViewState,
  onViewChanged,
  children,
  showSearchBox = false,
}: PropsWithChildren<MapProps>) {
  const { findMapStyle } = useMapStyle();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchReady, setSearchReady] = useState<MapboxGLMap | null>(null);

  // Keep track of the currently selected map style
  const [mapStyle, setMapStyle] = useState<MapStyle>(findMapStyle(defaultMapStyle));

  // Prepare the fallback initial view state
  const defaultViewState = {
    longitude: DEFAULT_LONGITUDE,
    latitude: DEFAULT_LATITUDE,
    zoom: DEFAULT_ZOOM,
    padding: { top: 10, bottom: 10, left: 10, right: 10 },
  };

  const searchBoxTheme = {
    variables: {
      boxShadow: 'none',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
    },
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Search box */}
      {showSearchBox && searchReady && (
        <div className="max-w-[50ch]">
          <SearchBox
            accessToken={ACCESS_TOKEN}
            map={searchReady}
            mapboxgl={mapboxgl}
            value={searchInputValue}
            onChange={setSearchInputValue}
            placeholder="Search for a location"
            theme={searchBoxTheme}
          />
        </div>
      )}

      <Mapbox.Map
        mapboxAccessToken={ACCESS_TOKEN}
        mapStyle={mapStyle.url}
        initialViewState={initialViewState ?? defaultViewState}
        onDragEnd={onViewChanged}
        onZoomEnd={onViewChanged}
        style={{ height, borderRadius: 6 }}
        cooperativeGestures
        onLoad={({ target }) => {
          setSearchReady(target);
        }}
      >
        {/* Show Mapbox map controls */}
        <Mapbox.FullscreenControl />
        <Mapbox.NavigationControl showCompass visualizePitch />
        <Mapbox.GeolocateControl
          trackUserLocation
          showUserLocation
          showUserHeading
          showAccuracyCircle={false}
        />

        {/* Show custom map controls */}
        <StyleControl selected={mapStyle} onSelect={setMapStyle} />

        {/* Render additional layers */}
        {children}
      </Mapbox.Map>
    </div>
  );
}

export default Map;
